import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { Box, Text } from "@chakra-ui/react";
import { SCOPE_COLORS } from "./KpiCharts";

// Horizontal collapsible tree (root → scopes → categories → sub-cats),
// inspired by amCharts' tree-chart demo. Click a node to collapse / expand
// its children. Hovering shows the path + emissions breakdown like the
// icicle tab. Uses d3 v7 (already in the project) — no new deps.

const HEIGHT = 460;
// Node width was 180px when the value label was a rounded "445k tCO2e";
// at full precision the label can run to ~25 characters at 13px (e.g.
// "444,785.81 tCO2e · 73.9%"), so 260px gives the value + percentage
// breathing room without truncation. Layout is horizontal — width here
// only affects per-level density, not how many children fit on a level.
const NODE_WIDTH = 260;
const NODE_HEIGHT = 52;
const NODE_GAP_X = 60;
const ROOT_FILL = "#E8E6E0";
const TEXT_DARK = "#1A202C";
const TEXT_LIGHT = "#FFFFFF";

const lighten = (hex, amount) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `#${[mix(r), mix(g), mix(b)].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
};

const SCOPE_SHADES = {
  "Scope 1": [SCOPE_COLORS.scope1.base, lighten(SCOPE_COLORS.scope1.base, 0.35), lighten(SCOPE_COLORS.scope1.base, 0.65)],
  "Scope 2": [SCOPE_COLORS.scope2.base, lighten(SCOPE_COLORS.scope2.base, 0.35), lighten(SCOPE_COLORS.scope2.base, 0.65)],
  "Scope 3": [SCOPE_COLORS.scope3.base, lighten(SCOPE_COLORS.scope3.base, 0.35), lighten(SCOPE_COLORS.scope3.base, 0.65)],
};

// Match by the leading "Scope N" so labelled variants (e.g.
// "Scope 2 (Market-based)") still resolve to the right colour ramp.
const rampForScopeName = (name) => {
  if (SCOPE_SHADES[name]) return SCOPE_SHADES[name];
  const match = Object.keys(SCOPE_SHADES).find((k) => String(name || "").startsWith(k));
  return match ? SCOPE_SHADES[match] : ["#888", "#aaa", "#ccc"];
};

const fillForNode = (d) => {
  if (d.depth === 0) return ROOT_FILL;
  let n = d;
  while (n.depth > 1) n = n.parent;
  const ramp = rampForScopeName(n.data.name);
  return ramp[Math.min(d.depth - 1, ramp.length - 1)];
};

const textColorFor = (fill) => {
  const h = fill.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return l > 0.6 ? TEXT_DARK : TEXT_LIGHT;
};

// Render the node value at full precision (matches the SummaryCards
// tiles above). Locale-formatted thousands separators + two decimals so
// the figure here reconciles exactly against the headline KPI cards.
const formatValue = (v) => {
  const n = Number(v) || 0;
  return `${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })} tCO2e`;
};

function EmissionsTree({ data }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const widthRef = useRef(0);
  const [hover, setHover] = useState(null);

  const root = useMemo(() => {
    if (!data) return null;
    const r = d3
      .hierarchy(data)
      .sum((d) => d.value || 0)
      .sort((a, b) => (b.value || 0) - (a.value || 0));
    // Initial state: root + its direct children (Scope 1/2/3) visible,
    // everything below that collapsed until the user clicks. Walking
    // post-order means we hide deeper levels first so when we reach the
    // scope nodes their children are already stashed on `_children`.
    r.eachAfter((d) => {
      if (d.depth >= 1 && d.children) {
        d._children = d.children;
        d.children = null;
      }
    });
    return r;
  }, [data]);

  useEffect(() => {
    if (!root || !svgRef.current || !containerRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const gLinks = svg.append("g").attr("class", "links").attr("fill", "none").attr("stroke", "#CBD5E0").attr("stroke-width", 1.2);
    const gNodes = svg.append("g").attr("class", "nodes");

    // Persisted previous positions for entry/exit animations.
    root.x0 = HEIGHT / 2;
    root.y0 = 0;

    const render = (source) => {
      const containerWidth = widthRef.current || 800;
      // d3.tree().nodeSize() gives us a fixed pitch per node, so the bounding
      // box scales naturally as branches expand or collapse.
      const treeLayout = d3.tree().nodeSize([NODE_HEIGHT + 14, NODE_WIDTH + NODE_GAP_X]);
      treeLayout(root);

      const nodes = root.descendants();
      const links = root.links();

      // Compute the full bounding box of the laid-out tree so we can size
      // the SVG to fit, expanding both vertically and horizontally as the
      // user drills in. d3 places nodes around (0,0) — x is the cross-axis
      // (vertical here, since the layout is horizontal), y is the depth.
      let minX = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      nodes.forEach((d) => {
        if (d.x < minX) minX = d.x;
        if (d.x > maxX) maxX = d.x;
        if (d.y > maxY) maxY = d.y;
      });
      const treeHeight = (maxX - minX) + NODE_HEIGHT;
      const treeWidth = maxY + NODE_WIDTH;
      const padX = 10;
      const padY = 16;
      // The SVG height grows to whatever the tree needs (with a sensible
      // minimum so a tiny initial state doesn't look squashed).
      const svgHeight = Math.max(HEIGHT, treeHeight + padY * 2);
      // SVG width grows past the container if needed; the wrapper has
      // overflowX:auto so it scrolls.
      const svgWidth = Math.max(containerWidth, treeWidth + padX * 2);

      // Centre the tree vertically inside the SVG; offsetX shifts everything
      // right so the root rectangle's left edge isn't cropped.
      const offsetY = (svgHeight - (maxX - minX)) / 2 - minX;
      const offsetX = padX;

      svg
        .attr("width", svgWidth)
        .attr("height", svgHeight)
        .attr("viewBox", `0 0 ${svgWidth} ${svgHeight}`);

      const t = svg.transition().duration(450).ease(d3.easeCubic);

      // Nodes ----------------------------------------------------------
      const nodeSel = gNodes.selectAll("g.node").data(nodes, (d) => d.__id ?? (d.__id = `${d.depth}-${d.data.name}-${Math.random()}`));

      const nodeEnter = nodeSel
        .enter()
        .append("g")
        .attr("class", "node")
        .attr("transform", () => `translate(${(source.y0 ?? source.y) + offsetX}, ${(source.x0 ?? source.x) + offsetY})`)
        .style("cursor", (d) => (d.children || d._children ? "pointer" : "default"))
        .on("click", (event, d) => {
          if (d.children) {
            d._children = d.children;
            d.children = null;
          } else if (d._children) {
            d.children = d._children;
            d._children = null;
          } else {
            return;
          }
          render(d);
        })
        .on("mousemove", (event, d) => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          const path = d.ancestors().reverse().slice(1).map((n) => n.data.name);
          const value = d.value || 0;
          const parentValue = d.parent?.value || 0;
          const pct = parentValue > 0 ? (value / parentValue) * 100 : null;
          setHover({
            mouseX: event.clientX - rect.left,
            mouseY: event.clientY - rect.top,
            containerW: rect.width,
            path: path.length ? path.join(" · ") : d.data.name,
            value,
            pct,
            parentName: d.parent?.data?.name,
          });
        })
        .on("mouseleave", () => setHover(null));

      nodeEnter
        .append("rect")
        .attr("class", "tile")
        .attr("rx", 6)
        .attr("ry", 6)
        .attr("width", NODE_WIDTH)
        .attr("height", NODE_HEIGHT)
        .attr("y", -NODE_HEIGHT / 2)
        .attr("fill", (d) => fillForNode(d))
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1)
        .attr("opacity", 0);

      nodeEnter
        .append("clipPath")
        .attr("id", (d) => `tree-clip-${d.__id}`)
        .append("rect")
        .attr("width", NODE_WIDTH - 16)
        .attr("height", NODE_HEIGHT)
        .attr("x", 8)
        .attr("y", -NODE_HEIGHT / 2);

      const labelG = nodeEnter
        .append("g")
        .attr("class", "label")
        .attr("clip-path", (d) => `url(#tree-clip-${d.__id})`)
        .style("pointer-events", "none");

      // Card layout: tCO2e value + percentage at equal weight on the top
      // line, name below. We reduce the tCO2e font slightly so the % can
      // sit on the same row at the same weight without crowding.
      labelG
        .append("text")
        .attr("class", "value")
        .attr("font-size", 13)
        .attr("font-weight", 700)
        .attr("dominant-baseline", "central")
        .attr("x", 10)
        .attr("y", -10)
        .attr("fill", (d) => textColorFor(fillForNode(d)))
        .text((d) => {
          const parentValue = d.parent?.value || 0;
          const pct = parentValue > 0 ? (d.value / parentValue) * 100 : null;
          const v = formatValue(d.value || 0);
          return pct != null ? `${v} · ${pct.toFixed(1)}%` : v;
        });

      labelG
        .append("text")
        .attr("class", "name")
        .attr("font-size", 11)
        .attr("font-weight", 500)
        .attr("dominant-baseline", "central")
        .attr("x", 10)
        .attr("y", 10)
        .attr("fill", (d) => textColorFor(fillForNode(d)))
        .attr("opacity", 0.85)
        .text((d) => d.data.name);

      // Disclosure indicator on the right edge for nodes that have
      // collapsible/expandable children. + when collapsed, − when open.
      nodeEnter
        .append("text")
        .attr("class", "toggle")
        .attr("font-size", 14)
        .attr("font-weight", 700)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("x", NODE_WIDTH - 12)
        .attr("y", 0)
        .attr("fill", (d) => textColorFor(fillForNode(d)))
        .style("pointer-events", "none")
        .text((d) => (d.children ? "−" : d._children ? "+" : ""));

      // Merge for updates ------------------------------------------------
      const nodeUpdate = nodeSel.merge(nodeEnter);
      nodeUpdate
        .transition(t)
        .attr("transform", (d) => `translate(${d.y + offsetX}, ${d.x + offsetY})`);
      nodeUpdate.select("rect.tile").transition(t).attr("opacity", 1);
      nodeUpdate.select("text.toggle").text((d) => (d.children ? "−" : d._children ? "+" : ""));

      nodeSel
        .exit()
        .transition(t)
        .attr("transform", () => `translate(${source.y + offsetX}, ${source.x + offsetY})`)
        .style("opacity", 0)
        .remove();

      // Links ----------------------------------------------------------
      const diagonal = (s, t2) => {
        // Right-angle elbow with rounded inflection — readable in trees of
        // this size, less visually noisy than fully-curved Béziers.
        const sx = s.y + offsetX + NODE_WIDTH;
        const sy = s.x + offsetY;
        const tx = t2.y + offsetX;
        const ty = t2.x + offsetY;
        const midX = (sx + tx) / 2;
        return `M ${sx} ${sy} C ${midX} ${sy}, ${midX} ${ty}, ${tx} ${ty}`;
      };

      const linkSel = gLinks.selectAll("path.link").data(links, (d) => `${d.source.__id}-${d.target.__id}`);
      const linkEnter = linkSel
        .enter()
        .append("path")
        .attr("class", "link")
        .attr("d", () => {
          const o = { x: source.x0 ?? source.x, y: source.y0 ?? source.y };
          return diagonal(o, o);
        })
        .attr("stroke-opacity", 0);
      linkSel
        .merge(linkEnter)
        .transition(t)
        .attr("stroke-opacity", 1)
        .attr("d", (d) => diagonal(d.source, d.target));
      linkSel
        .exit()
        .transition(t)
        .attr("d", () => {
          const o = { x: source.x, y: source.y };
          return diagonal(o, o);
        })
        .attr("stroke-opacity", 0)
        .remove();

      // Stash positions for the next transition's enter/exit anchors.
      nodes.forEach((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    };

    // Defer the render to the next frame to avoid Chrome's
    // "ResizeObserver loop completed with undelivered notifications"
    // warning, which webpack-dev surfaces as an uncaught error overlay.
    let rafId = null;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      if (w === 0) return;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        widthRef.current = w;
        render(root);
      });
    });
    ro.observe(containerRef.current);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [root]);

  return (
    <Box w="100%">
      <Text fontSize="12px" color="gray.500" mb={2}>
        Click a node to expand or collapse its children.
      </Text>
      <Box
        ref={containerRef}
        w="100%"
        position="relative"
        overflowX="auto"
        sx={{
          "&::-webkit-scrollbar": { height: "8px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
        }}
      >
        <svg ref={svgRef} style={{ display: "block" }} />
        {(() => {
          if (!hover) return null;
          const TIP_W = 260;
          const offset = 12;
          const fitsRight = hover.mouseX + offset + TIP_W <= hover.containerW;
          const left = fitsRight ? hover.mouseX + offset : Math.max(0, hover.mouseX - offset - TIP_W);
          const top = Math.min(Math.max(0, hover.mouseY + offset), HEIGHT - 60);
          return (
            <Box
              position="absolute"
              left={`${left}px`}
              top={`${top}px`}
              w={`${TIP_W}px`}
              bg="white"
              px={3}
              py={2}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              boxShadow="sm"
              pointerEvents="none"
              zIndex={10}
            >
              <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={2}>
                {hover.path}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {formatValue(hover.value)}
                {hover.pct != null && (
                  <Text as="span" color="gray.400" ml={2}>
                    · {hover.pct.toFixed(1)}% of {hover.parentName === "Total" ? "total" : hover.parentName}
                  </Text>
                )}
              </Text>
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
}

export default EmissionsTree;
